import {
  Bot,
  CalendarDays,
  CreditCard,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  MessageCircleQuestion,
  MessageSquareText,
  Package,
  ScrollText,
  Settings,
  ShieldCheck,
  Tags,
  Users,
  Workflow,
  Zap,
} from 'lucide-react'
import { RBAC_MODULES, TENANT_FEATURE_FLAGS } from '../../lib/rbac'

// Single source of truth for the desktop sidebar (SPEC-growchat-faq "Nav change D2").
// Item fields:
// - key: stable id, also used for the "seen" marker of the NEW badge
// - labelKey: i18n key; `verticalLabel` resolves it through the vertical-aware translator
// - route: path below the locale prefix
// - module: RBAC module required to see the item
// - featureFlag: optional tenant feature flag that must be on
// - available: false hides the item (no "coming soon" placeholders)
// - newUntil: optional 'YYYY-MM-DD'; the NEW badge shows until that day or the first visit
// - children: sub-entries of an expandable item. A parent has no module or flag of
//   its own: its visibility derives from its children (shown if any child is
//   visible), and its `route` is informational only (the shell opens the first
//   visible child when the sidebar is collapsed)
export const NAV_GROUPS = [
  {
    key: 'general',
    labelKey: 'nav.sectionGeneral',
    items: [
      { key: 'dashboard', labelKey: 'nav.dashboard', route: '/dashboard', icon: LayoutDashboard, module: RBAC_MODULES.dashboard, available: true },
      { key: 'conversations', labelKey: 'nav.conversations', route: '/inbox', icon: MessageSquareText, module: RBAC_MODULES.conversations, available: true },
      {
        key: 'customers',
        labelKey: 'nav.customerNav',
        route: '/customers',
        icon: Users,
        available: true,
        children: [
          { key: 'customers-list', labelKey: 'nav.customers', route: '/customers', module: RBAC_MODULES.contacts, available: true },
          { key: 'followers', labelKey: 'nav.followers', route: '/followers', module: RBAC_MODULES.contacts, available: true },
        ],
      },
      {
        key: 'transactions',
        labelKey: 'nav.transactions',
        // Unused by the shell: the collapsed click goes to the first visible child.
        route: '/bookings',
        icon: CalendarDays,
        available: true,
        children: [
          { key: 'bookings', labelKey: 'nav.bookings', verticalLabel: true, route: '/bookings', module: RBAC_MODULES.bookings, featureFlag: TENANT_FEATURE_FLAGS.bookings, available: true },
          { key: 'orders', labelKey: 'nav.orders', route: '/orders', module: RBAC_MODULES.orders, featureFlag: TENANT_FEATURE_FLAGS.orders, available: true },
        ],
      },
      { key: 'products', labelKey: 'nav.products', route: '/products', icon: Package, module: RBAC_MODULES.products, available: true },
    ],
  },
  {
    key: 'bot',
    labelKey: 'nav.sectionBot',
    items: [
      { key: 'bot-config', labelKey: 'nav.botConfig', route: '/bot-config', icon: Bot, module: RBAC_MODULES.botConfig, available: true },
      // FAQ, Playbook and bot logs are bot configuration surfaces. Visibility
      // is gated by bot access and the relevant tenant feature flag.
      { key: 'faq', labelKey: 'nav.faq', route: '/faq', icon: MessageCircleQuestion, module: RBAC_MODULES.botConfig, featureFlag: TENANT_FEATURE_FLAGS.faq, available: true, newUntil: '2026-11-12' },
      { key: 'playbook', labelKey: 'nav.playbook', route: '/playbook', icon: Workflow, module: RBAC_MODULES.botConfig, featureFlag: TENANT_FEATURE_FLAGS.playbook, available: true, newUntil: null },
      { key: 'quick-answers', labelKey: 'nav.quickAnswers', route: '/quick-answers', icon: Zap, module: RBAC_MODULES.botConfig, featureFlag: TENANT_FEATURE_FLAGS.quick_answer, available: true, newUntil: null },
      { key: 'bot-logs', labelKey: 'nav.botLogs', route: '/reply-log', icon: ScrollText, module: RBAC_MODULES.botConfig, featureFlag: TENANT_FEATURE_FLAGS.bot_logs, available: true, newUntil: null },
    ],
  },
  {
    key: 'marketing',
    labelKey: 'nav.sectionMarketing',
    items: [
      // Route and module are placeholders until the broadcast screen exists.
      { key: 'broadcast', labelKey: 'nav.broadcast', route: '/broadcasts', icon: Megaphone, module: RBAC_MODULES.automation, available: false, newUntil: null },
      { key: 'automations', labelKey: 'nav.automations', route: '/automations', icon: Zap, module: RBAC_MODULES.automation, available: true },
    ],
  },
  {
    key: 'settings',
    labelKey: 'nav.sectionSettings',
    items: [
      { key: 'tags', labelKey: 'nav.tags', route: '/tags', icon: Tags, module: RBAC_MODULES.contactTags, featureFlag: TENANT_FEATURE_FLAGS.contact_tags, available: true },
      { key: 'custom-statuses', labelKey: 'nav.customStatus', route: '/custom-statuses', icon: ListChecks, module: RBAC_MODULES.customStatus, featureFlag: TENANT_FEATURE_FLAGS.custom_status, available: true },
      { key: 'settings', labelKey: 'nav.advancedSettings', route: '/settings', icon: Settings, module: RBAC_MODULES.settings, available: true },
      { key: 'billing', labelKey: 'nav.creditManagement', route: '/billing', icon: CreditCard, module: RBAC_MODULES.billing, available: true },
      { key: 'admin-permissions', labelKey: 'nav.adminPermissions', route: '/admin-permissions', icon: ShieldCheck, module: RBAC_MODULES.adminPermissions, available: true },
    ],
  },
]

export function findNavItem(key, groups = NAV_GROUPS) {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.key === key) return item
      const child = item.children?.find((entry) => entry.key === key)
      if (child) return child
    }
  }
  return null
}

function isEntryVisible(entry, canAccessModule, isFlagOn) {
  if (!entry.available) return false
  if (entry.featureFlag && !isFlagOn(entry.featureFlag)) return false
  return !entry.module || canAccessModule(entry.module)
}

// Filters the config down to what the current user may see. Expandable items
// keep only their visible children; empty groups are dropped so no header renders.
export function resolveNavGroups(groups, { canAccessModule, isFlagOn }) {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => {
          if (!item.children) return isEntryVisible(item, canAccessModule, isFlagOn) ? item : null
          if (!item.available) return null
          const children = item.children.filter((child) => isEntryVisible(child, canAccessModule, isFlagOn))
          return children.length ? { ...item, children } : null
        })
        .filter(Boolean),
    }))
    .filter((group) => group.items.length > 0)
}

const SEEN_PREFIX = 'nav.seen.'

function defaultStorage() {
  try {
    return window.localStorage ?? null
  } catch {
    return null
  }
}

function hasSeen(key, storage) {
  try {
    return storage?.getItem(`${SEEN_PREFIX}${key}`) === '1'
  } catch {
    return false
  }
}

export function markNavItemSeen(key, storage = defaultStorage()) {
  try {
    storage?.setItem(`${SEEN_PREFIX}${key}`, '1')
  } catch {
    // Storage may be blocked (private mode, quota); the badge then stays until newUntil.
  }
}

function localIsoDate(date) {
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// The NEW badge shows on an available item while today <= newUntil and the
// item has not been opened in this browser yet.
export function isNavItemNew(item, { now = new Date(), storage = defaultStorage() } = {}) {
  if (!item?.available || !item.newUntil) return false
  if (localIsoDate(now) > item.newUntil) return false
  return !hasSeen(item.key, storage)
}
